/**
 * Decisions and Labels API client.
 */

import { BaseClient } from '../core/client';
import type { RequestOptions } from '../core/config';
import type {
  Decision,
  DecisionFilters,
  DecisionListResponse,
  RecordDecisionRequest,
  Label,
  ApplyLabelRequest,
  LabelListResponse,
} from './types';

export class DecisionsClient extends BaseClient {
  /**
   * Record a new decision for a customer.
   */
  async recordDecision(
    request: RecordDecisionRequest,
    requestOptions?: RequestOptions
  ): Promise<Decision> {
    return this.requestWithRetry<Decision>(
      '/api/v1/decisions',
      { method: 'POST', body: JSON.stringify(request) },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Get decisions for a customer.
   */
  async getDecisions(
    customerId: string,
    filters?: DecisionFilters,
    requestOptions?: RequestOptions
  ): Promise<DecisionListResponse> {
    const queryString = this.buildQueryString({
      customer_id: customerId,
      ...filters,
    });
    return this.requestWithRetry<DecisionListResponse>(
      `/api/v1/decisions${queryString}`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Get a specific decision by ID.
   */
  async getDecision(
    decisionId: string,
    requestOptions?: RequestOptions
  ): Promise<Decision> {
    return this.requestWithRetry<Decision>(
      `/api/v1/decisions/${encodeURIComponent(decisionId)}`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Apply a label to a customer.
   */
  async applyLabel(
    request: ApplyLabelRequest,
    requestOptions?: RequestOptions
  ): Promise<Label> {
    return this.requestWithRetry<Label>(
      '/api/v1/labels',
      { method: 'POST', body: JSON.stringify(request) },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Remove a label from a customer.
   */
  async removeLabel(
    customerId: string,
    requestOptions?: RequestOptions
  ): Promise<void> {
    return this.requestWithRetry<void>(
      `/api/v1/labels/${encodeURIComponent(customerId)}`,
      { method: 'DELETE' },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Get labels for a customer.
   */
  async getLabels(
    customerId: string,
    requestOptions?: RequestOptions
  ): Promise<LabelListResponse> {
    const queryString = this.buildQueryString({ customer_id: customerId });
    return this.requestWithRetry<LabelListResponse>(
      `/api/v1/labels${queryString}`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }
}
